
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Save, Euro, Moon, Calendar } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface HourlyCost {
  id: string;
  trietia: number;
  cost_per_hour: number;
}

export const HourlyCostsSettings = () => {
  const [regularCosts, setRegularCosts] = useState<HourlyCost[]>([]);
  const [nightCosts, setNightCosts] = useState<HourlyCost[]>([]);
  const [holidayCosts, setHolidayCosts] = useState<HourlyCost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminId, setAdminId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchAllCosts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Get current user and their parent admin (if contributor)
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");

        const { data: parentAdminId } = await supabase.rpc('get_parent_admin', { user_id: user.id });
        const effectiveAdminId = parentAdminId || user.id;
        setAdminId(effectiveAdminId);
        
        // Fetch regular costs for this admin
        const { data: regularData, error: regularError } = await supabase
          .from("hourly_costs")
          .select("*")
          .eq("admin_id", effectiveAdminId)
          .order("trietia");
          
        if (regularError) throw regularError;

        // If no costs exist, initialize them
        if (!regularData || regularData.length === 0) {
          await supabase.rpc('initialize_admin_hourly_costs', { p_admin_id: effectiveAdminId });
          
          // Fetch again after initialization
          const { data: newRegularData } = await supabase
            .from("hourly_costs")
            .select("*")
            .eq("admin_id", effectiveAdminId)
            .order("trietia");
          setRegularCosts(newRegularData || []);
        } else {
          setRegularCosts(regularData);
        }

        // Fetch night costs
        const { data: nightData, error: nightError } = await supabase
          .from("night_hourly_costs")
          .select("*")
          .eq("admin_id", effectiveAdminId)
          .order("trietia");
          
        if (nightError) throw nightError;
        setNightCosts(nightData || []);

        // Fetch holiday costs
        const { data: holidayData, error: holidayError } = await supabase
          .from("holiday_hourly_costs")
          .select("*")
          .eq("admin_id", effectiveAdminId)
          .order("trietia");
          
        if (holidayError) throw holidayError;
        setHolidayCosts(holidayData || []);

      } catch (error: any) {
        console.error("Error fetching costs:", error);
        setError(error.message || "Failed to load costs data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllCosts();
  }, []);

  const handleCostChange = (type: string, trietia: number, value: string) => {
    const newValue = parseFloat(value) || 0;
    switch (type) {
      case 'regular':
        setRegularCosts(prev => prev.map(cost => 
          cost.trietia === trietia ? { ...cost, cost_per_hour: newValue } : cost
        ));
        break;
      case 'night':
        setNightCosts(prev => prev.map(cost => 
          cost.trietia === trietia ? { ...cost, cost_per_hour: newValue } : cost
        ));
        break;
      case 'holiday':
        setHolidayCosts(prev => prev.map(cost => 
          cost.trietia === trietia ? { ...cost, cost_per_hour: newValue } : cost
        ));
        break;
    }
  };

  const saveCosts = async (type: string) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const tableName = type === 'regular' ? 'hourly_costs' : 
                       type === 'night' ? 'night_hourly_costs' : 
                       'holiday_hourly_costs';
      
      const costsData = type === 'regular' ? regularCosts :
                       type === 'night' ? nightCosts :
                       holidayCosts;

      for (const cost of costsData) {
        const { error } = await supabase
          .from(tableName)
          .update({ cost_per_hour: cost.cost_per_hour, updated_at: new Date().toISOString() })
          .eq("id", cost.id);

        if (error) throw error;
      }

      toast({
        title: "Hourly costs updated",
        description: `The ${type} hourly costs have been successfully updated.`,
      });
    } catch (error: any) {
      console.error(`Error updating ${type} costs:`, error);
      setError(error.message || `Failed to update ${type} costs`);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update ${type} costs. Please try again.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Hourly Costs</CardTitle>
          <CardDescription>Loading hourly costs...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Filter costs to only show trietia 0-3
  const filteredRegularCosts = regularCosts.filter(cost => cost.trietia >= 0 && cost.trietia <= 3);
  const filteredNightCosts = nightCosts.filter(cost => cost.trietia >= 0 && cost.trietia <= 3);
  const filteredHolidayCosts = holidayCosts.filter(cost => cost.trietia >= 0 && cost.trietia <= 3);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hourly Costs Configuration</CardTitle>
        <CardDescription>
          Set the hourly cost rates for each τριετία level and shift type
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="regular" className="space-y-4">
          <TabsList>
            <TabsTrigger value="regular" className="flex items-center gap-2">
              <Euro className="h-4 w-4" />
              <span>Regular</span>
            </TabsTrigger>
            <TabsTrigger value="night" className="flex items-center gap-2">
              <Moon className="h-4 w-4" />
              <span>Night</span>
            </TabsTrigger>
            <TabsTrigger value="holiday" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Holiday</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="regular">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredRegularCosts.map((cost) => (
                  <div key={cost.id} className="flex flex-col space-y-2">
                    <label className="text-sm font-medium">
                      Τριετία: {cost.trietia}
                    </label>
                    <div className="flex items-center">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={cost.cost_per_hour}
                        onChange={(e) => handleCostChange('regular', cost.trietia, e.target.value)}
                        className="pr-10"
                      />
                      <Euro className="h-4 w-4 -ml-8 text-gray-500" />
                      <span className="ml-1 text-gray-500">/ hour</span>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => saveCosts('regular')} 
                disabled={isSubmitting}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Regular Rates
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="night">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredNightCosts.map((cost) => (
                  <div key={cost.id} className="flex flex-col space-y-2">
                    <label className="text-sm font-medium">
                      Τριετία: {cost.trietia}
                    </label>
                    <div className="flex items-center">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={cost.cost_per_hour}
                        onChange={(e) => handleCostChange('night', cost.trietia, e.target.value)}
                        className="pr-10"
                      />
                      <Euro className="h-4 w-4 -ml-8 text-gray-500" />
                      <span className="ml-1 text-gray-500">/ hour</span>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => saveCosts('night')} 
                disabled={isSubmitting}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Night Rates
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="holiday">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredHolidayCosts.map((cost) => (
                  <div key={cost.id} className="flex flex-col space-y-2">
                    <label className="text-sm font-medium">
                      Τριετία: {cost.trietia}
                    </label>
                    <div className="flex items-center">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={cost.cost_per_hour}
                        onChange={(e) => handleCostChange('holiday', cost.trietia, e.target.value)}
                        className="pr-10"
                      />
                      <Euro className="h-4 w-4 -ml-8 text-gray-500" />
                      <span className="ml-1 text-gray-500">/ hour</span>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => saveCosts('holiday')} 
                disabled={isSubmitting}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Holiday Rates
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default HourlyCostsSettings;
